import type { PromotionPrerequisiteIssue, StructuredError } from './types';

/** Codes whose backend message is shown to the user as-is, keeping the code. */
const PASS_THROUGH_CODES = [
  'MISSING_CONTENT',
  'AI_INPUT_INVALID',
  'MISSING_PDF',
  'MISSING_COMMITTED_INVESTMENTS',
] as const;

type PassThroughCode = (typeof PASS_THROUGH_CODES)[number];

function isPassThroughCode(code: string): code is PassThroughCode {
  return (PASS_THROUGH_CODES as readonly string[]).includes(code);
}

export function parseStructuredError(error: {
  code: string;
  error: string;
  issues?: PromotionPrerequisiteIssue[];
}): StructuredError {
  if (isPassThroughCode(error.code)) {
    return { error: error.error, code: error.code, structuredError: true };
  }
  switch (error.code) {
    case 'INVALID_FIELDS': {
      const invalidFields = JSON.parse(error.error.split('INVALID_FIELDS:')[1].trim()).fieldErrors;
      return { invalidFields, structuredError: true };
    }
    case 'MISSING_REQUIRED_FIELDS': {
      const fields = error.error.split('Missing required fields:')[1].trim().split(', ');
      return { missingFields: fields, structuredError: true };
    }
    case 'DUPLICATE_RECORD': {
      const duplicateFields = error.error
        .split('Record already exists with:')[1]
        .trim()
        .split(', ');
      return { duplicateFields, structuredError: true };
    }
    case 'INVALID_INPUT':
      return {
        error:
          JSON.parse(error.error.split('INVALID_INPUT:')[1].trim()).message ||
          'Unable to process request',
        structuredError: true,
      };
    case 'PROMOTION_PREREQUISITES':
      return {
        code: 'PROMOTION_PREREQUISITES',
        error: error.error,
        issues: Array.isArray(error.issues) ? error.issues : [],
        structuredError: true,
      };
    case 'MISSING_EOI_INVESTOR':
      return {
        error: 'At least one EOI submission needs to exist to promote opportunity',
      };
    default:
      return { error: 'Unable to process request' };
  }
}

/**
 * The backend's message from a rejected request (a string or a structured error).
 * Anything else, such as a JS Error from a transport failure, gets the fallback.
 */
export function backendErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error) return error;
  if (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string') {
    return error.error;
  }
  return fallback;
}
